import { BusMessage, MessageFactory, BOOLEAN_BYTES_PER_ELEMENT, UID_SAFE_BYTES_PER_ELEMENT, calcArrayByteLength, calcStringByteLength, calcObjectByteLength } from "flavr-common";
/**
 * Announces that speech-to-text recording started.
 */
export class STTStartedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === STTStartedMessage.typestatic;
    }
}
STTStartedMessage.typestatic = "STTStartedMessage";
STTStartedMessage.subjectstatic = "stt";
MessageFactory.register(STTStartedMessage);
/**
 * Announces that speech-to-text recording ended.
 */
export class STTEndedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === STTEndedMessage.typestatic;
    }
}
STTEndedMessage.typestatic = "STTEndedMessage";
STTEndedMessage.subjectstatic = "stt";
MessageFactory.register(STTEndedMessage);
// Speech to text message has the recognition text from a speech
/**
 * Carries recognized speech-to-text results.
 */
export class STTMessage extends BusMessage {
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + calcArrayByteLength(this.results);
    }
    constructor(results, uid) {
        super();
        this.results = results;
        this.uid = uid;
    }
    serialization(stream) {
        this.results = stream.array(this.results);
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === STTMessage.typestatic;
    }
}
STTMessage.typestatic = "STTMessage";
STTMessage.subjectstatic = "stt";
MessageFactory.register(STTMessage);
/**
 * Starts speech-to-text recording for a model or interaction context.
 */
export class StartSTTRecordMessage extends BusMessage {
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === StartSTTRecordMessage.typestatic;
    }
}
StartSTTRecordMessage.typestatic = "StartSTTRecordMessage";
StartSTTRecordMessage.subjectstatic = "stt";
MessageFactory.register(StartSTTRecordMessage);
/**
 * Stops the current speech-to-text recording.
 */
export class StopSTTRecordMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StopSTTRecordMessage.typestatic;
    }
}
StopSTTRecordMessage.typestatic = "StopSTTRecordMessage";
StopSTTRecordMessage.subjectstatic = "stt";
MessageFactory.register(StopSTTRecordMessage);
/**
 * Requests a speech-to-text recognition cycle.
 */
export class RequestSTTMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(waitForFinalResult = false) {
        super();
        this.waitForFinalResult = waitForFinalResult;
    }
    serialization(stream) {
        this.waitForFinalResult = stream.boolean(this.waitForFinalResult);
    }
    static is(msg) {
        return msg.type === RequestSTTMessage.typestatic;
    }
}
RequestSTTMessage.typestatic = "RequestSTTMessage";
RequestSTTMessage.subjectstatic = "stt";
MessageFactory.register(RequestSTTMessage);
// Message from SpeechParser, with a command structur from the STTMessage
/**
 * Carries a parsed speech-to-text command.
 */
export class STTCommandMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.cmd) + calcStringByteLength(this.matchWord) + calcObjectByteLength(this.parameters) + UID_SAFE_BYTES_PER_ELEMENT;
    }
    constructor(cmd, matchWord, parameters, uid) {
        super();
        this.cmd = cmd;
        this.matchWord = matchWord;
        this.parameters = parameters;
        this.uid = uid;
    }
    serialization(stream) {
        this.cmd = stream.utf16String(this.cmd);
        this.matchWord = stream.utf16String(this.matchWord);
        this.parameters = stream.object(this.parameters);
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === STTCommandMessage.typestatic;
    }
}
STTCommandMessage.typestatic = "STTCommandMessage";
STTCommandMessage.subjectstatic = "stt";
MessageFactory.register(STTCommandMessage);
/**
 * Registers a speech command so it can be recognized and routed.
 */
export class STTRegisterCommand extends BusMessage {
    get byteLength() {
        return calcObjectByteLength(this.cmd);
    }
    constructor(cmd) {
        super();
        this.cmd = cmd;
    }
    serialization(stream) {
        this.cmd = stream.object(this.cmd);
    }
    static is(msg) {
        return msg.type === STTRegisterCommand.typestatic;
    }
}
STTRegisterCommand.typestatic = "STTRegisterCommand";
STTRegisterCommand.subjectstatic = "stt";
MessageFactory.register(STTRegisterCommand);
/**
 * Unregisters a previously registered speech command.
 */
export class STTUnregisterCommand extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.cmdName);
    }
    constructor(cmdName) {
        super();
        this.cmdName = cmdName;
    }
    serialization(stream) {
        this.cmdName = stream.utf16String(this.cmdName);
    }
    static is(msg) {
        return msg.type === STTUnregisterCommand.typestatic;
    }
}
STTUnregisterCommand.typestatic = "STTUnregisterCommand";
STTUnregisterCommand.subjectstatic = "stt";
MessageFactory.register(STTUnregisterCommand);
// This message create a voice from text
/**
 * Sends text that should be spoken through text-to-speech.
 */
export class TTSMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.text);
    }
    constructor(text) {
        super();
        this.text = text;
    }
    serialization(stream) {
        this.text = stream.utf16String(this.text);
    }
    static is(msg) {
        return msg.type === TTSMessage.typestatic;
    }
}
TTSMessage.typestatic = "TTSMessage";
TTSMessage.subjectstatic = "stt";
MessageFactory.register(TTSMessage);
/**
 * Requests annotation content derived from speech-to-text input.
 */
export class STTAnnotationRequestMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === STTAnnotationRequestMessage.typestatic;
    }
}
STTAnnotationRequestMessage.typestatic = "STTAnnoationRequestMessage";
STTAnnotationRequestMessage.subjectstatic = "stt";
MessageFactory.register(STTAnnotationRequestMessage);
/**
 * Returns annotation content derived from speech-to-text input.
 */
export class STTAnnotationAnswerMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.result);
    }
    constructor(result) {
        super();
        this.result = result;
    }
    serialization(stream) {
        this.result = stream.utf16String(this.result);
    }
    static is(msg) {
        return msg.type === STTAnnotationAnswerMessage.typestatic;
    }
}
STTAnnotationAnswerMessage.typestatic = "STTAnnoationAnswerMessage";
STTAnnotationAnswerMessage.subjectstatic = "stt";
MessageFactory.register(STTAnnotationAnswerMessage);
/**
 * Requests editing an annotation in a dialog through speech-to-text flow.
 */
export class STTEditAnnotationInDialogRequestMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.currentAnnotation);
    }
    constructor(currentAnnotation) {
        super();
        this.currentAnnotation = currentAnnotation;
    }
    serialization(stream) {
        this.currentAnnotation = stream.utf16String(this.currentAnnotation);
    }
    static is(msg) {
        return msg.type === STTEditAnnotationInDialogRequestMessage.typestatic;
    }
}
STTEditAnnotationInDialogRequestMessage.typestatic = "STTEditAnnotationInDialogRequestMessage";
STTEditAnnotationInDialogRequestMessage.subjectstatic = "stt";
MessageFactory.register(STTEditAnnotationInDialogRequestMessage);
